// src/FollowUpChips.tsx
import React from "react";
import { makeStyles, tokens } from "@fluentui/react-components";
import { getBrandConfig } from '../brands/brand-registry';

interface Props {
  questions: string[];
  onFollowUpClick: (question: string) => void;
}

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexWrap: "wrap",
    gap: "var(--chip-followup-gap, 4px)",
    marginTop: "var(--chip-followup-margin-top, 8px)",
    marginBottom: "var(--chip-followup-margin-bottom, 4px)",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    '@media (max-width: 600px)': {
      gap: 'var(--chip-followup-gap-mobile, 2px)',
      marginTop: 'var(--chip-followup-margin-top-mobile, 2px)',
      marginBottom: 'var(--chip-followup-margin-bottom-mobile, 2px)',
    },
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: "var(--chip-followup-height, 44px)", // WCAG AAA: 44px minimum (changed from 36px)
    paddingTop: "var(--chip-followup-padding-vertical, 8px)",
    paddingBottom: "var(--chip-followup-padding-vertical, 8px)",
    paddingLeft: "var(--chip-followup-padding-horizontal, 12px)",
    paddingRight: "var(--chip-followup-padding-horizontal, 12px)",
    backgroundColor: "var(--brand-chip-bg, var(--neutral-20))",
    border: "1px solid rgba(0, 0, 0, 0.1)",
    borderRadius: "var(--radius-button, 8px)",
    fontSize: "var(--chip-text-size, 14px)",
    fontFamily: "var(--font-family-base, \"Segoe UI\", \"Helvetica Neue\", Arial, sans-serif)",
    fontWeight: "var(--chip-text-weight, 400)",
    fontStyle: "var(--chip-text-style, normal)",
    lineHeight: "1.5", // WCAG AAA: 1.5× line height (1.4.8)
    color: "var(--text-primary, #000000)",
    cursor: "pointer",
    transition: "all 0.2s ease-in-out",
    flex: "1 1 auto", // Allow chips to grow and shrink to fill space
    minWidth: "fit-content", // Minimum width based on content
    maxWidth: "calc(50% - 4px)", // Maximum 50% minus gap for 2 chips per row
    wordWrap: "break-word",
    overflowWrap: "break-word",
    whiteSpace: "nowrap", // Prevent text wrapping within chips
    textOverflow: "ellipsis", // Add ellipsis if text is too long
    overflow: "hidden", // Hide overflow text
    ":hover": {
      backgroundColor: "var(--brand-chip-hover-bg, var(--neutral-30))",
      border: "1px solid rgba(0, 0, 0, 0.15)",
      color: "var(--text-primary, #000000)",
      transform: "translateY(-1px)",
      boxShadow: tokens.shadow4,
    },
    ":active": {
      transform: "translateY(0px)",
      boxShadow: tokens.shadow2,
    },
    '@media (max-width: 600px)': {
      minHeight: 'var(--chip-followup-height-mobile, 36px)',
      paddingTop: 'var(--chip-followup-padding-vertical-mobile, 4px)',
      paddingBottom: 'var(--chip-followup-padding-vertical-mobile, 4px)',
      paddingLeft: 'var(--chip-followup-padding-horizontal-mobile, 8px)',
      paddingRight: 'var(--chip-followup-padding-horizontal-mobile, 8px)',
      fontSize: 'var(--chip-text-size-mobile, 12px)',
      // Mobile: Allow text to wrap instead of truncating
      whiteSpace: 'normal',
      textOverflow: 'clip',
      overflow: 'visible',
      minWidth: '100%',
      maxWidth: '100%',
      textAlign: 'left',
    },
  }
});

export const FollowUpChips: React.FC<Props> = ({
  questions,
  onFollowUpClick,
}) => {
  const styles = useStyles();
  const brandConfig = getBrandConfig();
  if (!questions.length) return null;

  return (
    <div className={styles.container}>
      {questions.map((question, index) => (
        <div
          key={index}
          className={styles.chip}
          onClick={() => onFollowUpClick(question)}
          role="button"
          tabIndex={0}
          aria-label={`${brandConfig.followUp.ariaLabelPrefix}${question}`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onFollowUpClick(question);
            }
          }}
        >
          {question}
        </div>
      ))}
    </div>
  );
};
